import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AutomationTestsService } from '@eredes/auth';
import { Dsm34LoaderService } from '@eredes/dsm34';
import { take } from 'rxjs';

import { UserModel } from '../_core/models/user.model';
import { GlobalService } from '../_core/services/global.service';
import { GoogleAnalyticsEventsService } from '../_core/services/google-analytics-events.service';
import { ProfileService } from '../_core/services/profile.service';
import { UserService } from '../_core/services/user.service';

import { AuthService } from './auth.service';
import { AuthViewModel } from './auth.view-model';

import { MessagingService } from 'app/messaging/_services/messaging.service';

const EDP_DOMAIN_LIST = ['e-redes.pt', 'edp.pt', 'edp.com'];

@Component({
  selector: 'app-auth',
  templateUrl: './auth.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
})
export class AuthComponent implements OnInit {
  viewModel: AuthViewModel = new AuthViewModel();
  private recaptchaCheckedEmail: string | null = null;

  private readonly _dsm34LoaderService = inject(Dsm34LoaderService);
  private readonly authService = inject(AuthService);
  private readonly automationTestsService = inject(AutomationTestsService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly globalService = inject(GlobalService);
  private readonly profileService = inject(ProfileService);
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);

  constructor() {
    this.prepareLoginTemplateInfo();
  }

  ngOnInit(): void {
    const _isMessagingOrd = this.userService.getUser()?.messaging;

    this.viewModel.isMessagingOrd = _isMessagingOrd
      ? MessagingService.hasMessagingAccess(_isMessagingOrd, ['ordconsumer', 'ordproducer'])
      : false;
    this.viewModel.skipAutomationRecaptcha = this.automationTestsService.skipReCaptchaValidation.getValue();

    this.globalService.getUser().subscribe((data) => {
      this.globalService.getUserClaims().subscribe((res) => {
        this.setUser(data, res);
      });
    });
  }

  /**
   * Block submit button when email or password and recaptcha are invalid
   * @returns {boolean} - true if email is invalid or if password and recaptcha (when required) are invalid, otherwise false
   */
  blockSubmitButton(): boolean {
    if (this.viewModel.formControls.username.invalid) {
      return true;
    }

    if (
      this.viewModel.showPassword &&
      this.viewModel.formControls.username.valid &&
      (this.viewModel.formControls.password.invalid ||
        (this.viewModel.showReCaptcha && this.viewModel.formControls.recaptcha.invalid))
    ) {
      return true;
    }

    return false;
  }

  /**
   * Validates whether a typed email is internal or external.
   * This method verify if email has internal domains like: e-redes.pt, edp.pt, edp.com or eedp.edp.pt.
   */
  validateLogin(): void {
    const usernameControl = this.viewModel.formControls.username;
    if (usernameControl.invalid) {
      usernameControl.markAsTouched();

      return;
    }

    const email: string = usernameControl.value.toLowerCase();
    const isEdpDomainList = EDP_DOMAIN_LIST.some((domain) => email.includes(domain));
    this.viewModel.loginError = false;
    this.viewModel.showLoginErrorMessage = false;
    this.viewModel.showPassword = !isEdpDomainList;

    if (!this.viewModel.showPassword) {
      this.recaptchaCheckedEmail = null;
      this.viewModel.showReCaptcha = false;
      this.viewModel.setRecaptchaValidator(false);
      this.onLogIn();

      return;
    }

    if (this.viewModel.showReCaptcha && !this.isRecaptchaComponentLoaded()) {
      return;
    }

    if (this.recaptchaCheckedEmail !== email) {
      this.resolveRecaptchaRequirement(email);

      return;
    }

    if (this.viewModel.logInForm.controls.password.invalid) {
      this.viewModel.logInForm.controls.password.markAsTouched();

      return;
    }

    if (this.viewModel.showReCaptcha && this.viewModel.logInForm.controls.recaptcha.invalid) {
      this.viewModel.logInForm.controls.recaptcha.markAsTouched();

      return;
    }

    this.loginWithPassword(
      this.viewModel.formControls.username.value,
      this.viewModel.formControls.password.value,
      this.viewModel.showReCaptcha ? this.viewModel.formControls.recaptcha.value : null
    );
  }

  private resolveRecaptchaRequirement(email: string): void {
    this.viewModel.isLoading = true;
    this._dsm34LoaderService.show();
    this.authService
      .skipReCaptcha(email)
      .pipe(take(1))
      .subscribe({
        next: (result) => {
          const requiresRecaptcha = this.viewModel.skipAutomationRecaptcha ? false : !result;
          this.viewModel.setRecaptchaValidator(requiresRecaptcha);
          this.viewModel.showReCaptcha = requiresRecaptcha;
          this.recaptchaCheckedEmail = email;
        },
        error: () => {
          this._dsm34LoaderService.hide();
          this.viewModel.isLoading = false;
          this.cdr.detectChanges();
        },
        complete: () => {
          this._dsm34LoaderService.hide();
          this.viewModel.isLoading = false;
          this.cdr.detectChanges();
        },
      });
  }

  private isRecaptchaComponentLoaded(): boolean {
    return Boolean(globalThis.grecaptcha?.render);
  }

  /**
   * The `onLogIn` function handles the login process, checking if the environment is local or not and
   * making API calls accordingly.
   */
  private onLogIn(): void {
    const info = this.viewModel.logInForm.value;
    this.viewModel.username = info.username;
    if (this.viewModel.isEnvLoal) {
      this.globalService.loginWithPassword(this.viewModel.username, null, null).subscribe((data) => {
        this.globalService.getUserClaims().subscribe((res) => {
          this.setUser(data, res);
        });
      });
    } else {
      this.globalService.getUser(this.viewModel.username).subscribe({
        next: (data) => {
          this.globalService.getUserClaims().subscribe((res) => {
            this.setUser(data, res);
          });
        },
        error: () => {
          this.viewModel.loginError = true;
          GoogleAnalyticsEventsService.emitEvent(
            'Página de Login',
            'Login',
            'Acede ao URL da área de Comercializadores mas não consegue autenticar-se'
          );
          globalThis.location.href = '/listeners/adfs/login/';
        },
      });
    }
  }

  /**
   * Function responsible for check route's data and matching important information
   */
  private prepareLoginTemplateInfo(): void {
    this.router.config.forEach((route: any) => {
      // forward slash concat is important because url doesn't have the first slash
      if (this.router.url === '/' + route.path && route.data?.messagingProfiles) {
        if (route.data.messagingProfiles.length > 0) {
          this.viewModel.messagingProfiles = route.data.messagingProfiles;
        }
      }
    });
  }

  /**
   * The setUser function sets the user data and claims, emits a Google Analytics event, stores the user
   * data in session storage, and checks the user data.
   * @param {any} data - The `data` parameter is of type `any` and represents the response data received
   * from an API call or any other data source.
   * @param {any} claim - The `claim` parameter is an object that represents the user's claims. It
   * contains information about the user's permissions or roles within the system.
   */
  private setUser(data: any, claim: any): void {
    const res: { userAccount: UserModel } = data.Result;
    res.userAccount.userClaims = claim;
    if (res) {
      GoogleAnalyticsEventsService.emitEvent(
        'Página de Login',
        'Login',
        'Acede ao URL da área de Comercializadores e consegue autenticar'
      );
      this.viewModel.loginError = false;
      sessionStorage.setItem('currentUser', JSON.stringify(res.userAccount));
      this.checkUserData();
    } else {
      GoogleAnalyticsEventsService.emitEvent(
        'Página de Login',
        'Login',
        'Acede ao URL da área de Comercializadores mas não consege autenticar-se'
      );
      this.viewModel.loginError = true;
    }
  }

  /**
   * The function checks the user data and sets the profile and permissions accordingly, then navigates
   * to the appropriate page based on the user's profile.
   */
  private checkUserData(): void {
    const currentUser = this.userService.getUser();

    if (currentUser?.userAccount) {
      this.profileService.setProfileByRoleAndPermissions(currentUser.profileName, currentUser.userPermissions);
      if (this.userService.isProfileOrd() || this.userService.isProfileBpo()) {
        this.router.navigate(['/home']);
      } else {
        this.router.navigate(['/interlocutors']);
      }
    }
  }

  /**
   * Performs user login with email and password.
   * @param {string} username - The user's email
   * @param {string} password - The user's password
   * @param {string} verifyToken - The reCaptcha token
   */
  private loginWithPassword(username: string, password: string, verifyToken: string): void {
    this.viewModel.isLoading = true;
    if (this.viewModel.showReCaptcha) {
      grecaptcha.reset();
      this.viewModel.formControls.recaptcha.reset();
    }

    this.globalService
      .loginWithPassword(username, password, verifyToken)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.viewModel.isLoading = false;
          this.cdr.detectChanges();
          this.globalService.getUserClaims().subscribe((res) => {
            this.setUser(response, res);
          });
          this.router.navigate(['/home']);
        },
        error: () => {
          this.viewModel.isLoading = false;
          this.viewModel.showLoginErrorMessage = true;
          this.cdr.detectChanges();
        },
      });
  }
}
